import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PencomFooter } from "@/components/layout/PencomFooter";
import { PencomRoutes } from "@/router/PencomRoutes";
import { useDebounce } from "@/hooks/useDebounce";
import {
  useCompanyComplianceDetails,
  useSearchCompanies,
} from "@/hooks/verify-compliance/verify-compliance.hook";
import type { VerifyComplianceSearchResult } from "@/models/responses/verify-compliance/verify-compliance.response";
import CompanySearch from "@/components/verify-compliance/CompanySearch";
import CompanyComplianceCard from "@/components/verify-compliance/CompanyComplianceCard";
import NoCompaniesFound from "@/components/verify-compliance/NoCompaniesFound";
import { Spinner } from "@/components/verify-compliance/icons";

const ComplianceVerificationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pccParam = searchParams.get("pcc") ?? "";

  const [searchTerm, setSearchTerm] = useState(pccParam);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  // True while we're auto-resolving a deep-linked PCC (e.g. from a QR code scan).
  const [autoSelecting, setAutoSelecting] = useState(!!pccParam);

  const debouncedTerm = useDebounce(searchTerm.trim());

  const {
    data: suggestions = [],
    isFetching: isSearching,
    isFetched: isSearchFetched,
  } = useSearchCompanies(debouncedTerm);
  const { data: details, isFetching: isLoadingDetails } =
    useCompanyComplianceDetails(selectedCode);

  // When opened via a QR code URL (?pcc=…), wait for the debounced search to
  // complete (isFetched=true) then automatically select the first result.
  // We gate on isFetched — not just !isSearching — because on the first render
  // react-query hasn't started the fetch yet and isSearching is still false.
  useEffect(() => {
    if (!autoSelecting || isSearching || !isSearchFetched) return;
    if (suggestions.length > 0) {
      const first = suggestions[0];
      setSelectedCode(first.employerCode);
      setSearchTerm(first.companyName);
      setShowSuggestions(false);
    }
    setAutoSelecting(false);
  }, [autoSelecting, isSearching, isSearchFetched, suggestions]);

  const showNoResults = useMemo(
    () =>
      !autoSelecting &&
      showSuggestions &&
      debouncedTerm.length >= 2 &&
      !isSearching &&
      suggestions.length === 0,
    [autoSelecting, showSuggestions, debouncedTerm, isSearching, suggestions.length]
  );

  const handleSelect = (company: VerifyComplianceSearchResult) => {
    setSelectedCode(company.employerCode);
    setSearchTerm(company.companyName);
    setShowSuggestions(false);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col font-clash-display">
      <main className="flex-1 px-4 sm:px-6 py-6 sm:py-10">
        <div className="max-w-4xl mx-auto">
          <button
            type="button"
            onClick={() => navigate(PencomRoutes.LOGIN)}
            className="inline-flex items-center gap-x-1 font-clash-display font-semibold text-md text-primary-button hover:opacity-80 transition-opacity cursor-pointer mb-8"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back
          </button>

          <div className="text-center mb-8 sm:mb-12">
            <div className="flex items-center justify-center mx-auto mb-5 sm:mb-6">
              <img
                src="/assets/images/onboarding/pencom_logo_transparent.png"
                alt="PenCom"
                className="h-16 w-auto sm:h-20 object-contain"
              />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Verify a company&apos;s compliance status
            </h1>
            <p className="text-gray-600 mb-6 sm:mb-8 text-sm sm:text-base">
              Search for a company to check their PenCom compliance status
            </p>

            <CompanySearch
              value={searchTerm}
              onChange={(value) => {
                setSearchTerm(value);
                setShowSuggestions(true);
                setAutoSelecting(false);
              }}
              onFocus={() => setShowSuggestions(true)}
              suggestions={suggestions}
              isSearching={isSearching}
              showSuggestions={!autoSelecting && showSuggestions}
              onSelect={handleSelect}
            />
          </div>

          {(autoSelecting || (selectedCode && isLoadingDetails)) && (
            <div className="flex justify-center py-12">
              <Spinner className="h-8 w-8 text-emerald-600" />
            </div>
          )}

          {selectedCode && !isLoadingDetails && !autoSelecting && details && (
            <CompanyComplianceCard details={details} />
          )}

          {showNoResults && <NoCompaniesFound term={debouncedTerm} />}
        </div>
      </main>

      <PencomFooter />
    </div>
  );
};

export default ComplianceVerificationPage;
